import type { NextPage } from "next";
import type { AppProps } from "next/app";
import { useEffect, useState, type ReactElement, type ReactNode } from "react";
import { NextIntlClientProvider } from "next-intl";
import { useRouter } from "next/router";
import localFont from "next/font/local";
import NextNProgress from "nextjs-progressbar";
import { Button } from "@repo/ui/button";
import "../styles/globals.css";
import "react-quill-new/dist/quill.snow.css";
import "leaflet/dist/leaflet.css";
import { ChevronUp } from "lucide-react";
import { motion } from "framer-motion";
import { useReportWebVitals } from "next/web-vitals";
import { AppContextProvider } from "@repo/app/AppContextProvider";
import { observer } from "@rekajs/react";
import { useApp } from "@repo/app/useApp";
import { Inter } from "next/font/google";
import { SettingStoreProvider } from "@repo/setting-store/provider";
import { Default } from "../layouts/default";

const inter = Inter({
  subsets: ["latin"],
  variable: "--font-inter",
});

// const doran = localFont({
//   src: [
//     {
//       path: "./fonts/doran/fonts/woff2/DoranFaNum-Bold.woff2",
//       weight: "700",
//       style: "normal",
//     },
//     {
//       path: "./fonts/doran/fonts/woff2/DoranFaNum-Light.woff2",
//       weight: "300",
//       style: "normal",
//     },
//     {
//       path: "./fonts/doran/fonts/woff2/DoranFaNum-Medium.woff2",
//       weight: "500",
//       style: "normal",
//     },
//     {
//       path: "./fonts/doran/fonts/woff2/DoranFaNum-Thin.woff2",
//       weight: "100",
//       style: "normal",
//     },
//     {
//       path: "./fonts/doran/fonts/woff2/DoranFaNum-Regular.woff2",
//       weight: "400",
//       style: "normal",
//     },
//     {
//       path: "./fonts/doran/fonts/woff2/DoranFaNum-ExtraBold.woff2",
//       weight: "800",
//       style: "normal",
//     },
//   ],
//   variable: "--font-doran",
// });

const doran = localFont({
  src: [
    {
      path: "./fonts/iran-yekan-x/woff2/IRANYekanX-Regular.woff2",
      weight: "500",
      style: "normal",
    },
    {
      path: "./fonts/iran-yekan-x/woff2/IRANYekanX-Bold.woff2",
      weight: "700",
      style: "normal",
    },
  ],
  variable: "--font-doran",
});

const helvetica = localFont({
  src: [
    {
      path: "./fonts/helvetica/helvetica-neue-lt-std-33-thin-extended.otf",
      style: "normal",
    },
  ],
  variable: "--font-helvetica",
});

const dirooz = localFont({
  src: [
    {
      path: "./fonts/dirooz/Dirooz-FD.woff2",
    },
  ],
  variable: "--font-dirooz",
});

const AppContent = observer((props: { children: React.ReactNode }) => {
  const app = useApp();
  app.setRendererMode("release");

  return props.children;
});
export type NextPageWithLayout<P = object, IP = P> = NextPage<P, IP> & {
  getLayout?: (page: ReactElement) => ReactNode;
};

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};

const App = observer(
  ({ Component, pageProps }: AppPropsWithLayout): ReactNode => {
    const router = useRouter();
    const [showButton, setShowButton] = useState(false);
    useReportWebVitals((metric) => {
      console.log(metric);
    });

    useEffect(() => {
      const handleScroll = (): void => {
        if (window.scrollY > 200) {
          setShowButton(true);
        } else {
          setShowButton(false);
        }
      };

      window.addEventListener("scroll", handleScroll);

      return () => {
        window.removeEventListener("scroll", handleScroll);
      };
    }, []);

    const getLayout =
      Component.getLayout ?? ((page) => <Default>{page}</Default>);
    return (
      <div
        className={`${doran.variable} ${helvetica.variable} ${dirooz.variable} ${inter.variable}`}
      >
        <NextNProgress
          color="#f70"
          options={{ easing: "ease", speed: 500 }}
          startPosition={0.3}
        />
        <NextIntlClientProvider
          locale={router.locale ?? "en"}
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- disabling unsafe assignment and member access
          messages={pageProps.messages}
          timeZone="Asia/Tehran"
        >
          <AppContextProvider>
            <SettingStoreProvider {...pageProps}>
              <AppContent>{getLayout(<Component {...pageProps} />)}</AppContent>
            </SettingStoreProvider>
          </AppContextProvider>
        </NextIntlClientProvider>
        {showButton ? (
          <motion.div
            animate={{ opacity: 1, y: 0 }}
            className="web-fixed web-bottom-3 web-right-3 lg:web-bottom-10 lg:web-right-10 web-z-30 web-rounded-full web-size-14"
            exit={{ opacity: 0, y: 30 }}
            initial={{ opacity: 0, y: 30 }}
            transition={{ duration: 0.5 }}
          >
            <Button
              className="rounded-full w-full h-full"
              onClick={() => {
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              size="icon"
            >
              <ChevronUp size={40} />
            </Button>
          </motion.div>
        ) : null}
      </div>
    );
  }
);

export default App;
